import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Zap, Search } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { EventCard } from '../components/EventCard';
import { EventForm } from '../components/EventForm';
import { EventPreview } from '../components/EventPreview';
import { BoostModal } from '../components/BoostModal';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { isEventActive } from '../lib/helpers';
import { toast } from 'sonner';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  OrganizerPendingView,
  OrganizerRejectedView,
  OrganizerViewerView,
  OrganizerErrorView,
} from '../components/OrganizerStatusViews';
import { CATEGORIES } from '../constants/categories';

export const OrganizerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [boostingEvent, setBoostingEvent] = useState(null);
  const [showBoostOptions, setShowBoostOptions] = useState(null);
  const [filter, setFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const q = query(collection(db, 'events'), where('organizerId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      eventsData.sort((a, b) => {
        if (a.boosted && !b.boosted) return -1;
        if (!a.boosted && b.boosted) return 1;
        if (a.boosted && b.boosted) {
          const aValue = a.boostValue || 0;
          const bValue = b.boostValue || 0;
          if (aValue !== bValue) return bValue - aValue;
        }
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA;
      });
      setEvents(eventsData);
      setLoading(false);
    }, (error) => {
      console.error('[OrganizerDashboard] Erro no listener de eventos:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user?.uid]);

  // Auto-open modal when URL contains eventId (direct link / Flutter deep link)
  useEffect(() => {
    if (!eventId || loading) return;
    const event = events.find(e => e.id === eventId);
    if (event) {
      setPreviewEvent(event);
    } else {
      navigate('/organizer', { replace: true });
    }
  }, [eventId, loading, events, navigate]);

  // Auto-close modal when browser back button removes eventId from URL
  useEffect(() => {
    if (!eventId) setPreviewEvent(null);
  }, [eventId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenPreview = (event) => {
    setPreviewEvent(event);
    navigate(`/organizer/event/${event.id}`);
  };

  const handleClosePreview = () => {
    setPreviewEvent(null);
    navigate('/organizer', { replace: true });
  };

  const handleCreateEvent = async (formData, imageFile) => {
    // Date/time combination (embedding the hour into the Timestamp) is handled
    // by eventService, which mirrors the Flutter app's data format.
    const eventData = {
      ...formData,
      createdBy: user.uid
    };

    // Organizador cria eventos com status "pending" (padrÃ£o)
    const result = await eventService.createEvent(eventData, imageFile, 'pending');
    if (result.success) {
      toast.success('Evento criado com sucesso! Aguardando aprovação do administrador.');
    } else {
      toast.error('Erro ao criar evento');
    }
  };

  const handleUpdateEvent = async (formData, imageFile) => {
    // Date/time combination (embedding the hour into the Timestamp) is handled
    // by eventService, which mirrors the Flutter app's data format.
    const eventData = { ...formData };

    const result = await eventService.updateEvent(editingEvent.id, eventData, imageFile);
    if (result.success) {
      toast.success('Evento atualizado com sucesso!');
      setEditingEvent(null);
    } else {
      toast.error('Erro ao atualizar evento');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      const result = await eventService.deleteEvent(eventId);
      if (result.success) {
        toast.success('Evento excluído com sucesso!');
      } else {
        toast.error('Erro ao excluir evento');
      }
    }
  };

  // Boost completion handler - just reload events
  const handleBoostComplete = () => {
    setBoostingEvent(null);
  };

  const getEventStats = () => {
    return {
      total: events.length,
      pending: events.filter(e => e.status === 'pending').length,
      approved: events.filter(e => e.status === 'approved').length,
      rejected: events.filter(e => e.status === 'rejected').length
    };
  };

  const normalizeText = (text = '') =>
    text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const getFilteredEvents = () => {
    let filtered;

    if (filter === 'active') {
      // Ativos: aprovados e ainda não encerrados (now <= endDate + 1min).
      filtered = events.filter(e => e.status === 'approved' && isEventActive(e));
    } else if (filter === 'pending') {
      // Pendentes: nenhum filtro de horário — se está pendente, aparece.
      filtered = events.filter(e => e.status === 'pending');
    } else if (filter === 'rejected') {
      filtered = events.filter(e => e.status === 'rejected');
    } else if (filter === 'boosted') {
      const now = new Date();
      // Impulsionados: aprovados, com boost vigente, e ainda não encerrados.
      filtered = events.filter(e =>
        e.status === 'approved' &&
        e.boosted === true &&
        e.boostEnd?.toDate?.() > now &&
        isEventActive(e)
      );
    } else {
      // Todos: tudo exceto rejeitados, e ainda não encerrados.
      filtered = events.filter(e => e.status !== 'rejected' && isEventActive(e));
    }

    if (categoryFilter) {
      filtered = filtered.filter(e => e.category === categoryFilter);
    }

    if (debouncedQuery.trim()) {
      const q = normalizeText(debouncedQuery);
      filtered = filtered.filter(e =>
        normalizeText(e.title).includes(q) ||
        normalizeText(e.description).includes(q) ||
        normalizeText(e.location).includes(q)
      );
    }

    return filtered;
  };

  const organizerStatus = user?.organizerStatus;

  if (organizerStatus && organizerStatus !== 'approved') {
    let statusView;
    if (organizerStatus === 'pending') {
      statusView = <OrganizerPendingView />;
    } else if (organizerStatus === 'rejected') {
      statusView = <OrganizerRejectedView />;
    } else if (organizerStatus === 'none' || organizerStatus === 'viewer') {
      statusView = <OrganizerViewerView />;
    } else {
      statusView = <OrganizerErrorView />;
    }
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="hidden md:block w-72 shrink-0" aria-hidden="true" />
        <main className="flex-1 p-4 pt-16 md:p-8 overflow-y-auto flex items-center justify-center">
          {statusView}
        </main>
      </div>
    );
  }

  const stats = getEventStats();

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="hidden md:block w-72 shrink-0" aria-hidden="true" />

      <main className="flex-1 p-4 pt-16 md:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Meus Eventos</h1>
              <p className="text-muted-foreground">Gerencie seus eventos criados</p>
            </div>
            <Button onClick={() => setShowForm(true)} className="gap-2 shrink-0" data-testid="create-event-btn">
              <Plus className="w-4 h-4" />
              Novo Evento
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="text-sm text-muted-foreground mb-1">Total</p>
              <p className="text-3xl font-bold" data-testid="stat-total">{stats.total}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="text-sm text-muted-foreground mb-1">Pendentes</p>
              <p className="text-3xl font-bold text-yellow-600" data-testid="stat-pending">{stats.pending}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="text-sm text-muted-foreground mb-1">Aprovados</p>
              <p className="text-3xl font-bold text-green-600" data-testid="stat-approved">{stats.approved}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <p className="text-sm text-muted-foreground mb-1">Rejeitados</p>
              <p className="text-3xl font-bold text-red-600" data-testid="stat-rejected">{stats.rejected}</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                variant={filter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('active')}
              >
                Ativos
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                Todos
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pending')}
              >
                Pendentes
              </Button>
              <Button
                variant={filter === 'rejected' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('rejected')}
              >
                Rejeitados
              </Button>
              <Button
                variant={filter === 'boosted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('boosted')}
              >
                Impulsionados
              </Button>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="shrink-0 w-40 h-9 px-3 border border-border rounded-md bg-background text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring transition-all hover:border-muted-foreground/40"
              >
                <option value="">Todas as categorias</option>
                {CATEGORIES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Pesquisar por título, descrição ou local..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-10 pr-3 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all hover:border-muted-foreground/40"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Carregando eventos...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <p className="text-muted-foreground mb-4">VocÃª ainda nÃ£o criou nenhum evento</p>
              <Button onClick={() => setShowForm(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Criar Primeiro Evento
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-6" data-testid="events-grid">
              {/* getFilteredEvents() já aplica a regra de cada aba (status +
                  now <= endDate+1min quando aplicável); aqui só ordenamos. */}
              {(() => {
                const filtered = getFilteredEvents();

                // Sort: boosted first, then by date ascending
                const sortedEvents = [...filtered].sort((a, b) => {
                  const currentTime = new Date();
                  // Check if boosted AND boostEnd > now
                  const aBoosted = a.boosted === true && a.boostEnd?.toDate?.() > currentTime;
                  const bBoosted = b.boosted === true && b.boostEnd?.toDate?.() > currentTime;

                  // Boosted events come first
                  if (aBoosted && !bBoosted) return -1;
                  if (!aBoosted && bBoosted) return 1;

                  // Then sort by date ascending
                  const aDate = a.date?.toDate?.() || new Date(a.date);
                  const bDate = b.date?.toDate?.() || new Date(b.date);
                  return aDate - bDate;
                });

                if (sortedEvents.length === 0) {
                  return (
                    <div key="empty" className="col-span-full text-center py-12">
                      <p className="text-muted-foreground font-medium">Nenhum evento encontrado</p>
                      <p className="text-sm text-muted-foreground mt-1">Tente ajustar os filtros ou pesquisar outro termo.</p>
                    </div>
                  );
                }

                return sortedEvents.map((event) => {
                  // Check boost status using correct fields
                  const currentTime = new Date();
                  const isBoostActive = event.boosted === true && event.boostEnd?.toDate?.() > currentTime;
                  // Impulsionar só aparece para eventos ativos (ainda não iniciados) e não rejeitados
                  const canShowBoostButton = event.status !== 'rejected' && isEventActive(event);

                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      onPreview={() => handleOpenPreview(event)}
                      actions={
                        <>
                          {canShowBoostButton && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={`flex-1 gap-2 ${
                                isBoostActive
                                  ? 'bg-orange-100 text-orange-600 border-orange-300'
                                  : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isBoostActive) {
                                  setShowBoostOptions(event);
                                } else {
                                  setBoostingEvent(event);
                                }
                              }}
                              data-testid={`boost-event-${event.id}`}
                            >
                              <Zap className="w-4 h-4" />
                              {isBoostActive ? 'Impulsionado' : 'Impulsionar'}
                            </Button>
                          )}
                          {event.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-2"
                              onClick={() => setEditingEvent(event)}
                              data-testid={`edit-event-${event.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                              Editar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 gap-2 text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteEvent(event.id)}
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Excluir
                          </Button>
                        </>
                      }
                    />
                  );
                });
              })()}
            </div>
          )}
        </div>
      </main>

      {showForm && (
        <EventForm
          onClose={() => setShowForm(false)}
          onSubmit={handleCreateEvent}
          isAdmin={false}
        />
      )}

      {editingEvent && (
        <EventForm
          initialData={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSubmit={handleUpdateEvent}
          isAdmin={false}
        />
      )}

      {previewEvent && (
        <EventPreview
          event={previewEvent}
          onClose={handleClosePreview}
          onBoost={setBoostingEvent}
        />
      )}

      {boostingEvent && (
        <BoostModal
          event={boostingEvent}
          onClose={() => setBoostingEvent(null)}
          onBoostComplete={handleBoostComplete}
        />
      )}
      {showBoostOptions && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl w-[320px] space-y-4">

      <h2 className="text-lg font-semibold">Evento impulsionado ðŸ”¥</h2>

      <p className="text-sm text-muted-foreground">
        Esse evento jÃ¡ estÃ¡ impulsionado. O que deseja fazer?
      </p>

      <Button
        className="w-full"
        onClick={() => {
          setBoostingEvent(showBoostOptions);
          setShowBoostOptions(null);
        }}
      >
        Aumentar duração
      </Button>

      <Button
        variant="destructive"
        className="w-full"
        onClick={() => {
          setShowBoostOptions(null);
        }}
      >
        Cancelar impulsionamento
      </Button>

      <Button
        variant="ghost"
        className="w-full"
        onClick={() => setShowBoostOptions(null)}
      >
        Fechar
      </Button>

    </div>
  </div>
)}
    </div>
  );
};

